import {useId, useMemo, useState, type KeyboardEvent} from 'react';
import {useIntl} from 'react-intl';
import {useSelector} from 'react-redux';
import {Search} from 'lucide-react';
import {selectUserId} from '../../../store/features/Auth/authSlice';
import {useFetchUsersQuery, type UserElement} from '../../../store/features/Users/userService';
import {CallAvatar} from '../components/CallAvatar';
import {prepMessages as m} from './prepMessages';

const MAX_RESULTS = 5;

export const teamUserId = (user: UserElement): string => String(user.userId ?? user.id);
export const teamUserName = (user: UserElement): string => [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email;

export interface PrepAddPeopleProps {
    /** PhoneHQ user ids already on the list; they never show up as results. */
    readonly excludeUserIds: ReadonlySet<string>;
    readonly onAdd: (user: UserElement) => void;
}

export const PrepAddPeople = ({excludeUserIds, onAdd}: PrepAddPeopleProps) => {
    const intl = useIntl();
    const listId = useId();
    const selfId = String(useSelector(selectUserId) ?? '');
    const [text, setText] = useState('');
    const [active, setActive] = useState(0);
    const phrase = text.trim().toLowerCase();
    const {data: users} = useFetchUsersQuery({}, {skip: !phrase});

    const results = useMemo(
        () => (users ?? [])
            .filter(user => teamUserId(user) !== selfId && !excludeUserIds.has(teamUserId(user)))
            .filter(user => teamUserName(user).toLowerCase().includes(phrase))
            .slice(0, MAX_RESULTS),
        [excludeUserIds, phrase, selfId, users],
    );

    const add = (user: UserElement | undefined) => {
        if (user) {
            onAdd(user);
            setText('');
            setActive(0);
        }
    };

    const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            const step = event.key === 'ArrowDown' ? 1 : -1;
            setActive(current => (results.length ? (current + step + results.length) % results.length : 0));
        } else if (event.key === 'Enter') {
            event.preventDefault();
            add(results[active]);
        }
    };

    return (
        <div className="relative pb-1">
            <label className="flex h-10 items-center gap-2 rounded-[10px] bg-[var(--phq-call-surface-muted)] px-3 text-[var(--phq-call-ink-muted)] focus-within:shadow-[inset_0_0_0_2px_var(--phq-call-brand-focus)]">
                <Search size={16} className="shrink-0" aria-hidden="true"/>
                <input
                    type="search"
                    role="combobox"
                    data-testid="prep-add-people"
                    aria-expanded={!!phrase}
                    aria-controls={listId}
                    aria-activedescendant={results[active] ? `${listId}-${teamUserId(results[active])}` : undefined}
                    aria-label={intl.formatMessage(m.addPeople)}
                    placeholder={intl.formatMessage(m.addPeople)}
                    value={text}
                    onChange={event => {
                        setText(event.target.value);
                        setActive(0);
                    }}
                    onKeyDown={onKeyDown}
                    className="min-w-0 flex-1 bg-transparent text-sm text-[var(--phq-call-ink)] outline-none placeholder:text-[var(--phq-call-ink-muted)]"
                />
            </label>
            {phrase && (
                <div
                    id={listId}
                    role="listbox"
                    aria-label={intl.formatMessage(m.addPeopleResults)}
                    className="phq-call-swap absolute left-0 right-0 top-[calc(100%)] z-10 flex flex-col gap-0.5 rounded-xl border border-solid border-[var(--phq-call-line)] bg-[var(--phq-call-surface)] p-1 phq-call-shadow-overlay"
                >
                    {results.length === 0 && <p className="px-2 py-2 text-[13px] text-[var(--phq-call-ink-muted)]">{intl.formatMessage(m.addPeopleNone)}</p>}
                    {results.map((user, index) => (
                        <div
                            key={teamUserId(user)}
                            id={`${listId}-${teamUserId(user)}`}
                            role="option"
                            aria-selected={index === active}
                            onMouseEnter={() => setActive(index)}
                            onClick={() => add(user)}
                            className={`flex h-11 cursor-pointer items-center gap-2.5 rounded-lg px-2 ${index === active ? 'bg-[var(--phq-call-brand-tint-subtle)]' : ''}`}
                        >
                            <CallAvatar identity={{name: teamUserName(user), userId: teamUserId(user)}} size={28}/>
                            <span className="min-w-0 flex-1">
                                <span className="block truncate text-sm font-medium">{teamUserName(user)}</span>
                                {user.position && <span className="block truncate text-xs text-[var(--phq-call-ink-muted)]">{user.position}</span>}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
